/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { v4 as uuidv4 } from 'uuid'

export type Searchable = Record<string, unknown> | string | []
export type Value = string | boolean | number

function isKeyMatch(keyTmp: string | undefined, searchKey: string): boolean {
  if (keyTmp === undefined) {
    return false
  }

  return keyTmp === searchKey || keyTmp.endsWith(`[${searchKey}]`)
}

/**
 * Find a or all path(s) to a key with a given value in an object
 * @param obj <Searchable> the object
 * @param key <string> the key to search for
 * @param value <Value> the value to search for
 */
export function findPaths(
  obj: Searchable,
  key?: string,
  value?: Value
): string | string[] | void {
  const results: string[] = []

  const find = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any,
    searchKey: string | undefined,
    searchValue: Value | undefined,
    pathToData: string
  ): string | void => {
    if (
      typeof data === 'string' ||
      typeof data === 'boolean' ||
      typeof data === 'number'
    ) {
      const keyTmp = pathToData.split('.').pop()
      if (
        searchKey &&
        ((searchValue !== undefined && data === searchValue) || !searchValue)
      ) {
        if (isKeyMatch(keyTmp, searchKey)) {
          results.push(pathToData)
        }
      } else if (
        searchKey === undefined &&
        searchValue !== undefined &&
        data === searchValue
      ) {
        if (keyTmp) {
          results.push(pathToData)
        }
      }
    } else if (Array.isArray(data)) {
      for (let j = 0; j < data.length; j++) {
        find(data[j], searchKey, searchValue, `${pathToData}[${j}]`)
      }
      return
    } else if (typeof data === 'object') {
      for (const k in data) {
        // eslint-disable-next-line no-prototype-builtins
        if (data.hasOwnProperty(k)) {
          find(
            data[k],
            searchKey,
            searchValue,
            `${pathToData ? pathToData + '.' : ''}${k}`
          )
        }
      }
      return
    }
  }

  find(obj, key, value, '')
  if (results.length > 1) {
    return results
  } else if (results.length === 1) {
    return results[0]
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function findAllByKey(obj: any, keyToFind: string): any[] {
  const result: any[] = []

  if (obj) {
    for (const [key, value] of Object.entries(obj)) {
      if (key.includes(keyToFind)) {
        result.push(value)
      } else if (typeof value === 'object') {
        result.push(...findAllByKey(value, keyToFind))
      }
    }
  }

  return result
}

interface NestedObject {
  [key: string]: any | NestedObject | NestedObject[]
}

export function findNestedKeyPath(
  obj: NestedObject,
  id: number,
  keyToSearch: string,
  path: string[] = []
): string[] | null {
  function hasKey(obj: NestedObject, key: string): [boolean, string[]] {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      return [true, [key]]
    }
    for (const [subKey, value] of Object.entries(obj)) {
      if (typeof value === 'object' && value !== null) {
        const [found, subPath] = hasKey(value, key)
        if (found) {
          return [true, [subKey, ...subPath]]
        }
      }
    }
    return [false, []]
  }

  for (const [key, value] of Object.entries(obj)) {
    const currentPath = [...path, key]
    if (key === 'id' && value === id) {
      const [found, keyPath] = hasKey(obj, keyToSearch)
      if (found) {
        const resultPath = [...currentPath.slice(0, -1), ...keyPath]
        return resultPath
      } else {
        return null
      }
    } else if (typeof value === 'object' && value !== null) {
      const result = findNestedKeyPath(value, id, keyToSearch, currentPath)
      if (result !== null) {
        return result
      }
    }
  }
  return null
}

export function getRandomString(): string {
  return uuidv4().slice(0, 6)
}

export function isObjectEmpty(object: Record<string, unknown>): boolean {
  for (const _ in object) {
    return false
  }

  return true
}
